import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, photoSrc } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSocket } from "../context/SocketContext";
import ProfileCard from "../components/ProfileCard";
import {
  LayoutDashboard, Users, Bot, Wallet, Banknote, Heart, MessageSquare,
  Settings as SettingsIcon, Search, X, Loader2, LogOut, ScrollText,
  ChevronLeft, RefreshCw, Trash2, Plus, Power, Pencil, Menu, UserRound, ShieldCheck, KeyRound, Copy, Check, ImagePlus,
} from "lucide-react";

const NAV = [
  ["dashboard", "Dashboard", LayoutDashboard],
  ["users", "Users", Users],
  ["real-users", "Real Users", UserRound],
  ["verifications", "Verification", ShieldCheck],
  ["password-resets", "Forgot Password", KeyRound],
  ["bots", "Bots", Bot],
  ["deposits", "Deposits", Wallet],
  ["withdrawals", "Withdrawals", Banknote],
  ["matches", "Matches", Heart],
  ["chats", "Chats", MessageSquare],
  ["logs", "Activity Log", ScrollText],
  ["settings", "Settings", SettingsIcon],
];

const fmtDate = (s) => (s ? new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");
const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export default function AdminGate() {
  const { user } = useAuth();
  if (user === null) {
    return <div className="min-h-[100dvh] bg-white grid place-items-center"><Loader2 className="w-7 h-7 animate-spin text-gray-400" /></div>;
  }
  if (user && user.is_admin) return <AdminPanel />;
  return <AdminLogin />;
}

function AdminLogin() {
  const { applyAuth, logout } = useAuth();
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { phone: phone.trim(), password });
      if (!data.user?.is_admin) {
        await logout();
        setErr("This account does not have admin access.");
      } else {
        applyAuth(data);
      }
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Invalid credentials");
    } finally { setBusy(false); }
  };

  const inp = "w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-gray-900";
  return (
    <div className="min-h-[100dvh] bg-gray-50 grid place-items-center p-4" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-sm bg-white border border-gray-200 rounded-xl p-7 shadow-sm">
        <h1 className="text-lg font-bold text-gray-900">GulMatch <span className="text-gray-400 font-normal">Admin</span></h1>
        <p className="text-sm text-gray-500 mt-1 mb-6">Authorized administrators only.</p>
        <div className="space-y-3">
          <input data-testid="admin-login-phone" className={inp} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Admin phone number" autoComplete="username" />
          <input data-testid="admin-login-password" type="password" className={inp} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" autoComplete="current-password" />
          {err && <p data-testid="admin-login-error" className="text-sm text-red-600">{err}</p>}
          <button data-testid="admin-login-submit" disabled={busy} className="w-full py-2.5 bg-gray-900 text-white rounded-md text-sm font-semibold disabled:opacity-50">{busy ? "Signing in…" : "Sign in"}</button>
        </div>
      </form>
    </div>
  );
}

function AdminPanel() {
  const { logout } = useAuth();
  const [section, setSection] = useState("dashboard");
  const [menuOpen, setMenuOpen] = useState(false);
  const [prPending, setPrPending] = useState(0);
  const [prRefresh, setPrRefresh] = useState(0);
  const socket = useSocket();

  // Pending forgot-password requests badge — polls so new requests are always visible.
  useEffect(() => {
    const fetchCount = () => api.get("/admin/password-resets")
      .then(({ data }) => setPrPending(data.counts?.pending || 0)).catch(() => {});
    fetchCount();
    const t = setInterval(fetchCount, 20000);
    return () => clearInterval(t);
  }, []);

  // Realtime: new forgot-password requests arrive instantly over WebSocket.
  useEffect(() => {
    if (!socket) return;
    return socket.subscribe((data) => {
      if (data.type === "password_reset_request") {
        setPrPending((c) => c + 1);
        setPrRefresh((k) => k + 1);
        toast.error(`New password reset request — +91 ${data.phone}`, { duration: 10000 });
      }
    });
  }, [socket]);

  return (
    <div className="min-h-[100dvh] bg-white text-gray-800 flex font-sans">
      {/* Sidebar */}
      <aside className={`${menuOpen ? "fixed inset-y-0 left-0 z-40 w-60" : "hidden"} md:flex md:static md:w-60 bg-gray-50 border-r border-gray-200 flex-col shrink-0`}>
        <div className="h-16 flex items-center px-5 border-b border-gray-200">
          <span className="text-lg font-bold tracking-tight text-gray-900">GulMatch <span className="text-gray-400 font-normal">Admin</span></span>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {NAV.map(([key, label, Icon]) => (
            <button key={key} data-testid={`admin-nav-${key}`} onClick={() => { setSection(key); setMenuOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${section === key ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-100"}`}>
              <Icon className="w-4.5 h-4.5" /> {label}
              {key === "password-resets" && prPending > 0 && (
                <span data-testid="pr-pending-badge"
                  className="ml-auto min-w-[1.4rem] h-[1.4rem] px-1 rounded-full bg-red-500 text-white text-xs font-bold grid place-items-center animate-pulse">
                  {prPending}
                </span>
              )}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-gray-200 space-y-1">
          <button data-testid="admin-logout" onClick={logout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-gray-600 hover:bg-gray-100"><LogOut className="w-4 h-4" /> Log out</button>
        </div>
      </aside>
      {menuOpen && <div className="fixed inset-0 bg-black/30 z-30 md:hidden" onClick={() => setMenuOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 border-b border-gray-200 flex items-center gap-3 px-4 md:px-6 shrink-0">
          <button className="md:hidden p-2 -ml-2 text-gray-600" onClick={() => setMenuOpen(true)}><Menu className="w-5 h-5" /></button>
          <h1 className="text-base font-semibold text-gray-900">{NAV.find(([k]) => k === section)?.[1] || section}</h1>
        </header>
        <main className="flex-1 overflow-y-auto p-4 md:p-6 bg-white">
          {section === "dashboard" && <Dashboard />}
          {section === "users" && <UsersSection />}
          {section === "real-users" && <RealUsersSection />}
          {section === "verifications" && <VerificationsSection />}
          {section === "password-resets" && <PasswordResetsSection onCounts={setPrPending} refreshKey={prRefresh} />}
          {section === "bots" && <BotsSection />}
          {section === "deposits" && <DepositsSection />}
          {section === "withdrawals" && <WithdrawalsSection />}
          {section === "matches" && <MatchesSection />}
          {section === "chats" && <ChatsSection />}
          {section === "logs" && <LogsSection />}
          {section === "settings" && <SettingsSection />}
        </main>
      </div>
    </div>
  );
}

/* ---------------- shared UI ---------------- */
const Spinner = () => <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
const Th = ({ children, className = "" }) => <th className={`text-left font-semibold text-gray-500 text-xs uppercase tracking-wide px-3 py-2.5 ${className}`}>{children}</th>;
const Td = ({ children, className = "", ...rest }) => <td className={`px-3 py-2.5 border-t border-gray-100 align-middle ${className}`} {...rest}>{children}</td>;

function Badge({ status }) {
  const map = {
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    approved: "bg-blue-50 text-blue-700 border-blue-200",
    completed: "bg-green-50 text-green-700 border-green-200",
    paid: "bg-green-50 text-green-700 border-green-200",
    rejected: "bg-red-50 text-red-700 border-red-200",
    failed: "bg-red-50 text-red-700 border-red-200",
    created: "bg-gray-50 text-gray-600 border-gray-200",
    active: "bg-green-50 text-green-700 border-green-200",
    disabled: "bg-red-50 text-red-700 border-red-200",
  };
  return <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded border capitalize ${map[status] || "bg-gray-50 text-gray-600 border-gray-200"}`}>{status}</span>;
}

/* ---------------- Dashboard ---------------- */
function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then(({ data }) => setD(data)).catch(() => toast.error("Failed to load")); }, []);
  if (!d) return <Spinner />;
  const cards = [
    ["Total users", d.total_users], ["New (7 days)", d.new_users_7d], ["Active users", d.active_users],
    ["Online now", d.online_now], ["Bot profiles", d.total_bots], ["Total matches", d.total_matches],
    ["Total chats (msgs)", d.total_messages], ["Deposits", money(d.total_deposits_amount)],
    ["Withdrawn (paid)", money(d.total_withdrawn_amount)], ["Pending withdrawals", `${d.pending_withdrawals_count} · ${money(d.pending_withdrawals_amount)}`],
    ["Pending verifications", d.pending_verifications],
  ];
  return (
    <div data-testid="admin-dashboard" className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map(([label, val]) => (
        <div key={label} className="border border-gray-200 rounded-lg p-4">
          <p className="text-xs text-gray-500 mb-1">{label}</p>
          <p className="text-2xl font-bold text-gray-900">{val}</p>
        </div>
      ))}
    </div>
  );
}

/* ---------------- Users ---------------- */
function UsersSection() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);

  const load = useCallback(async (query = "") => {
    setData(null);
    try { const { data } = await api.get("/admin/users", { params: { q: query, limit: 50 } }); setData(data); }
    catch { toast.error("Failed to load users"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-users">
      <form onSubmit={(e) => { e.preventDefault(); load(q); }} className="flex gap-2 mb-4 max-w-md">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="user-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or phone"
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-gray-900" />
        </div>
        <button data-testid="user-search-button" className="px-4 py-2 bg-gray-900 text-white rounded-md text-sm font-medium">Search</button>
      </form>
      {!data ? <Spinner /> : (
        <>
          <p className="text-xs text-gray-500 mb-2">{data.total} users</p>
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-gray-50"><tr><Th>Name</Th><Th>Phone</Th><Th>Age</Th><Th>Gender</Th><Th>Location</Th><Th>Deposit</Th><Th>Withdraw</Th><Th>Status</Th><Th>Joined</Th></tr></thead>
              <tbody>
                {data.users.map((u) => (
                  <tr key={u.user_id} data-testid={`user-row-${u.user_id}`} onClick={() => setSel(u.user_id)} className="cursor-pointer hover:bg-gray-50">
                    <Td className="font-medium text-gray-900">{u.name || "—"}</Td>
                    <Td>{u.phone || "—"}</Td><Td>{u.age || "—"}</Td><Td className="capitalize">{u.gender || "—"}</Td>
                    <Td>{u.neighborhood || "—"}</Td><Td>{money(u.deposit_balance)}</Td><Td>{money(u.withdrawal_balance)}</Td>
                    <Td><Badge status={u.disabled ? "disabled" : "active"} /></Td><Td className="text-gray-500 text-xs">{fmtDate(u.created_at)}</Td>
                  </tr>
                ))}
                {data.users.length === 0 && <tr><Td className="text-gray-400" >No users found</Td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
      {sel && <UserDetail userId={sel} onClose={() => setSel(null)} onChanged={() => load(q)} />}
    </div>
  );
}

function UserDetail({ userId, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(null);
  const [adj, setAdj] = useState({ balance_type: "deposit", amount: "", note: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setD(null);
    try { const { data } = await api.get(`/admin/users/${userId}`); setD(data); setEdit(data.user); }
    catch { toast.error("Failed to load user"); }
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/users/${userId}`, {
        name: edit.name, age: edit.age ? Number(edit.age) : undefined, gender: edit.gender,
        bio: edit.bio, neighborhood: edit.neighborhood, disabled: edit.disabled,
      });
      toast.success("User updated"); onChanged?.(); load();
    } catch { toast.error("Update failed"); } finally { setSaving(false); }
  };

  const applyAdjust = async () => {
    const amt = parseFloat(adj.amount);
    if (!amt) { toast.error("Enter an amount"); return; }
    try {
      await api.post(`/admin/users/${userId}/adjust-balance`, { balance_type: adj.balance_type, amount: amt, note: adj.note });
      toast.success("Balance adjusted"); setAdj({ ...adj, amount: "", note: "" }); onChanged?.(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Adjust failed"); }
  };

  const inp = "w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-gray-900";
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="user-detail">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full max-w-lg bg-white border-l border-gray-200 h-full overflow-y-auto">
        <div className="h-16 border-b border-gray-200 flex items-center justify-between px-5 sticky top-0 bg-white">
          <button onClick={onClose} className="flex items-center gap-1 text-sm text-gray-600"><ChevronLeft className="w-4 h-4" /> Back</button>
          <span className="text-sm font-semibold">User profile</span>
        </div>
        {!d || !edit ? <Spinner /> : (
          <div className="p-5 space-y-6">
            <div className="flex items-center gap-4">
              {edit.photos?.[0]?.url ? <img src={edit.photos[0].url} alt="" className="w-16 h-16 rounded-full object-cover border border-gray-200" />
                : <div className="w-16 h-16 rounded-full bg-gray-100 grid place-items-center text-gray-400 text-xl">{(edit.name || "?")[0]}</div>}
              <div>
                <p className="font-semibold text-gray-900">{edit.name}</p>
                <p className="text-sm text-gray-500">{edit.phone}</p>
                <p className="text-xs text-gray-400 mt-0.5">Joined {fmtDate(edit.created_at)}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="border border-gray-200 rounded-md p-3"><p className="text-xs text-gray-500">Deposit balance</p><p className="text-lg font-bold">{money(edit.deposit_balance)}</p></div>
              <div className="border border-gray-200 rounded-md p-3"><p className="text-xs text-gray-500">Withdrawal balance</p><p className="text-lg font-bold">{money(edit.withdrawal_balance)}</p></div>
            </div>

            {/* Edit form */}
            <div>
              <h3 className="text-sm font-semibold mb-2 text-gray-900">Edit profile</h3>
              <div className="space-y-2">
                <input data-testid="edit-name" className={inp} value={edit.name || ""} onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder="Name" />
                <div className="flex gap-2">
                  <input data-testid="edit-age" type="number" className={inp} value={edit.age || ""} onChange={(e) => setEdit({ ...edit, age: e.target.value })} placeholder="Age" />
                  <select data-testid="edit-gender" className={inp} value={edit.gender || ""} onChange={(e) => setEdit({ ...edit, gender: e.target.value })}>
                    <option value="">Gender</option><option value="male">Male</option><option value="female">Female</option><option value="other">Other</option>
                  </select>
                </div>
                <input data-testid="edit-location" className={inp} value={edit.neighborhood || ""} onChange={(e) => setEdit({ ...edit, neighborhood: e.target.value })} placeholder="Location" />
                <textarea data-testid="edit-bio" className={inp} rows={2} value={edit.bio || ""} onChange={(e) => setEdit({ ...edit, bio: e.target.value })} placeholder="Bio" />
                <label className="flex items-center gap-2 text-sm text-gray-700"><input data-testid="edit-disabled" type="checkbox" checked={!!edit.disabled} onChange={(e) => setEdit({ ...edit, disabled: e.target.checked })} /> Account disabled (blocks login)</label>
                {edit.interests?.length ? <p className="text-xs text-gray-500">Interests: {edit.interests.join(", ")}</p> : null}
                <button data-testid="save-user" onClick={save} disabled={saving} className="px-4 py-2 bg-gray-900 text-white rounded-md text-sm font-medium disabled:opacity-50">{saving ? "Saving…" : "Save changes"}</button>
              </div>
            </div>

            {/* Balance adjust */}
            <div>
              <h3 className="text-sm font-semibold mb-2 text-gray-900">Adjust balance</h3>
              <div className="flex flex-wrap gap-2 items-end">
                <select data-testid="adjust-type" className={`${inp} w-32`} value={adj.balance_type} onChange={(e) => setAdj({ ...adj, balance_type: e.target.value })}>
                  <option value="deposit">Deposit</option><option value="withdrawal">Withdrawal</option>
                </select>
                <input data-testid="adjust-amount" type="number" className={`${inp} w-28`} value={adj.amount} onChange={(e) => setAdj({ ...adj, amount: e.target.value })} placeholder="± amount" />
                <input data-testid="adjust-note" className={`${inp} flex-1 min-w-[120px]`} value={adj.note} onChange={(e) => setAdj({ ...adj, note: e.target.value })} placeholder="Note (optional)" />
                <button data-testid="apply-adjust" onClick={applyAdjust} className="px-4 py-2 border border-gray-900 text-gray-900 rounded-md text-sm font-medium">Apply</button>
              </div>
              <p className="text-xs text-gray-400 mt-1">Use a negative amount to deduct. Records a transaction.</p>
            </div>

            {/* Matches */}
            <div>
              <h3 className="text-sm font-semibold mb-2 text-gray-900">Matches ({d.matches.length})</h3>
              <div className="space-y-1">
                {d.matches.map((m) => (
                  <div key={m.match_id} className="flex justify-between text-sm border border-gray-100 rounded px-3 py-1.5">
                    <span>{m.other?.name || "—"} {m.other?.is_bot ? <span className="text-xs text-gray-400">(bot)</span> : ""}</span>
                    <span className="text-gray-500">{m.message_count} msgs</span>
                  </div>
                ))}
                {d.matches.length === 0 && <p className="text-xs text-gray-400">No matches</p>}
              </div>
            </div>

            {/* Transactions */}
            <div>
              <h3 className="text-sm font-semibold mb-2 text-gray-900">Transactions ({d.transactions.length})</h3>
              <div className="space-y-1">
                {d.transactions.map((t) => (
                  <div key={t.txn_id} className="flex justify-between text-sm border border-gray-100 rounded px-3 py-1.5">
                    <span className="capitalize">{t.type.replace(/_/g, " ")}</span>
                    <span className="text-gray-600">{money(t.amount)} · {fmtDate(t.created_at)}</span>
                  </div>
                ))}
                {d.transactions.length === 0 && <p className="text-xs text-gray-400">No transactions</p>}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- Real Users ---------------- */
function RealUsersSection() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);

  const load = useCallback(async (query = "") => {
    setData(null);
    try { const { data } = await api.get("/admin/users", { params: { q: query, limit: 100 } }); setData(data); }
    catch { toast.error("Failed to load users"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-real-users">
      <form onSubmit={(e) => { e.preventDefault(); load(q); }} className="flex gap-2 mb-4 max-w-md">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="real-user-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or phone"
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-gray-900" />
        </div>
        <button data-testid="real-user-search-button" className="px-4 py-2 bg-gray-900 text-white rounded-md text-sm font-medium">Search</button>
      </form>
      {!data ? <Spinner /> : (
        <>
          <p className="text-xs text-gray-500 mb-2">{data.total} real users · click a row to view their full GulMatch profile</p>
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-gray-50"><tr><Th>Photo</Th><Th>Name</Th><Th>Phone</Th><Th>Age</Th><Th>Gender</Th><Th>Location</Th><Th>Joined</Th></tr></thead>
              <tbody>
                {data.users.map((u) => (
                  <tr key={u.user_id} data-testid={`real-user-row-${u.user_id}`} onClick={() => setSel(u.user_id)} className="cursor-pointer hover:bg-gray-50">
                    <Td>
                      {u.photos?.[0]?.url
                        ? <img src={photoSrc(u.photos[0].url)} alt="" className="w-9 h-9 rounded-full object-cover border border-gray-200" />
                        : <div className="w-9 h-9 rounded-full bg-gray-100 grid place-items-center text-gray-400 text-sm">{(u.name || "?")[0]}</div>}
                    </Td>
                    <Td className="font-medium text-gray-900">{u.name || "—"}</Td>
                    <Td>{u.phone || "—"}</Td><Td>{u.age || "—"}</Td><Td className="capitalize">{u.gender || "—"}</Td>
                    <Td>{u.neighborhood || "—"}</Td><Td className="text-gray-500 text-xs">{fmtDate(u.created_at)}</Td>
                  </tr>
                ))}
                {data.users.length === 0 && <tr><Td className="text-gray-400">No real users found</Td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
      {sel && <RealUserProfile userId={sel} onClose={() => setSel(null)} />}
    </div>
  );
}

function RealUserProfile({ userId, onClose }) {
  const [u, setU] = useState(null);
  useEffect(() => {
    api.get(`/admin/users/${userId}`).then(({ data }) => setU(data.user)).catch(() => toast.error("Failed to load profile"));
  }, [userId]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="real-user-profile">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-[#0D0C12] rounded-2xl border border-gray-200 w-full max-w-sm overflow-hidden">
        <div className="h-14 px-4 flex items-center justify-between border-b border-white/10">
          <span className="text-sm font-semibold text-white">{u ? u.name : "Profile"}</span>
          <button data-testid="real-user-profile-close" onClick={onClose} className="w-9 h-9 grid place-items-center text-slate-300 rounded-full hover:bg-white/10"><X className="w-5 h-5" /></button>
        </div>
        {!u ? (
          <div className="h-[60vh] grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
        ) : (
          <div className="p-4">
            <div className="relative w-full h-[62vh] max-h-[560px]">
              <ProfileCard profile={u} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- Verification (Aadhaar) ---------------- */
function VerificationsSection() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("pending");
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    setData(null);
    try { const { data } = await api.get("/admin/verifications", { params: filter === "all" ? {} : { status: filter } }); setData(data); }
    catch { toast.error("Failed to load verifications"); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-verifications">
      <div className="flex gap-2 mb-4 flex-wrap">
        {["pending", "approved", "rejected", "all"].map((s) => (
          <button key={s} data-testid={`ver-filter-${s}`} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-md text-sm capitalize ${filter === s ? "bg-gray-900 text-white" : "border border-gray-300 text-gray-600"}`}>
            {s}{data?.counts && s !== "all" ? ` (${data.counts[s] ?? 0})` : ""}
          </button>
        ))}
      </div>
      {!data ? <Spinner /> : (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="w-full text-sm min-w-[820px]">
            <thead className="bg-gray-50"><tr><Th>User</Th><Th>Phone</Th><Th>Profile name</Th><Th>Name on doc</Th><Th>Status</Th><Th>Submitted</Th><Th>Actions</Th></tr></thead>
            <tbody>
              {data.verifications.map((v) => (
                <tr key={v.verification_id} data-testid={`ver-row-${v.verification_id}`} className="hover:bg-gray-50">
                  <Td className="font-medium text-gray-900">{v.user?.name || "—"}</Td>
                  <Td>{v.user?.phone || "—"}</Td>
                  <Td>{v.profile_name || "—"}</Td>
                  <Td className="text-gray-600">{v.name_on_document || "—"}</Td>
                  <Td><Badge status={v.status} /></Td>
                  <Td className="text-gray-500 text-xs">{fmtDate(v.created_at)}</Td>
                  <Td><button data-testid={`ver-review-${v.verification_id}`} onClick={() => setOpen(v)} className="px-2.5 py-1 text-xs border border-gray-900 text-gray-900 rounded font-medium">Review</button></Td>
                </tr>
              ))}
              {data.verifications.length === 0 && <tr><Td className="text-gray-400">No verification requests</Td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {open && <VerificationReview v={open} onClose={() => setOpen(null)} onChanged={() => { setOpen(null); load(); }} />}
    </div>
  );
}

function VerificationReview({ v, onClose, onChanged }) {
  const [docUrl, setDocUrl] = useState(null);
  const [docErr, setDocErr] = useState(false);
  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let url;
    api.get(`/admin/verifications/${v.verification_id}/document`, { responseType: "blob" })
      .then((res) => { url = URL.createObjectURL(res.data); setDocUrl(url); })
      .catch(() => setDocErr(true));
    if (v.user?.user_id) {
      api.get(`/admin/users/${v.user.user_id}`).then(({ data }) => setProfile(data.user)).catch(() => {});
    }
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [v]);

  const act = async (action) => {
    let note = "";
    if (action === "reject") note = window.prompt("Reason for rejection (optional):") ?? "";
    setBusy(true);
    try {
      await api.put(`/admin/verifications/${v.verification_id}`, { action, admin_note: note });
      toast.success(`Verification ${action === "approve" ? "approved" : "rejected"}`);
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="ver-review-modal">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full max-w-xl bg-white border-l border-gray-200 h-full overflow-y-auto">
        <div className="h-16 border-b border-gray-200 flex items-center justify-between px-5 sticky top-0 bg-white z-10">
          <button onClick={onClose} className="flex items-center gap-1 text-sm text-gray-600"><ChevronLeft className="w-4 h-4" /> Back</button>
          <span className="text-sm font-semibold">Aadhaar verification</span>
        </div>
        <div className="p-5 space-y-6">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="border border-gray-200 rounded-md p-3"><p className="text-xs text-gray-500">Profile name</p><p className="font-semibold text-gray-900">{v.profile_name || "—"}</p></div>
            <div className="border border-gray-200 rounded-md p-3"><p className="text-xs text-gray-500">Name on document</p><p className="font-semibold text-gray-900">{v.name_on_document || "—"}</p></div>
          </div>
          {v.ai_reason && <p className="text-xs text-gray-500">AI check: {v.ai_reason}</p>}

          <div>
            <h3 className="text-sm font-semibold mb-2 text-gray-900">Submitted Aadhaar document</h3>
            {docErr ? <p className="text-sm text-red-600">Could not load document.</p>
              : !docUrl ? <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
              : <img data-testid="ver-document-image" src={docUrl} alt="Aadhaar document" className="w-full rounded-lg border border-gray-200" />}
          </div>

          {profile && (
            <div>
              <h3 className="text-sm font-semibold mb-2 text-gray-900">User profile</h3>
              <div className="relative w-full max-w-xs mx-auto" style={{ aspectRatio: "3/4" }}>
                <div className="absolute inset-0 rounded-2xl overflow-hidden bg-[#0D0C12]">
                  <ProfileCard profile={profile} />
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-2 pb-4">
            <Badge status={v.status} />
            {v.status === "pending" && (
              <div className="flex gap-2 ml-auto">
                <button data-testid="ver-reject" disabled={busy} onClick={() => act("reject")} className="px-4 py-2 text-sm border border-red-300 text-red-700 rounded-md font-medium disabled:opacity-50">Reject</button>
                <button data-testid="ver-approve" disabled={busy} onClick={() => act("approve")} className="px-4 py-2 text-sm bg-gray-900 text-white rounded-md font-medium disabled:opacity-50">Approve</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Forgot Password Requests (manual reset) ---------------- */
function PasswordResetsSection({ onCounts, refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("pending");
  const [links, setLinks] = useState({});      // request_id -> reset link
  const [copied, setCopied] = useState("");
  const [busyId, setBusyId] = useState("");

  const load = useCallback(async () => {
    setData(null);
    try {
      const { data } = await api.get("/admin/password-resets", { params: filter === "all" ? {} : { status: filter } });
      setData(data);
      onCounts?.(data.counts?.pending ?? 0);
    }
    catch { toast.error("Failed to load requests"); }
  }, [filter, onCounts]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const generate = async (r) => {
    setBusyId(r.request_id);
    const wasPending = r.status === "pending";
    try {
      const { data } = await api.post(`/admin/password-resets/${r.request_id}/generate-link`);
      setLinks((m) => ({ ...m, [r.request_id]: data.reset_link }));
      // Keep the row visible so the admin can copy the link — update status locally.
      setData((d) => d ? {
        ...d,
        requests: d.requests.map((x) => x.request_id === r.request_id ? { ...x, status: "link_sent" } : x),
        counts: d.counts && wasPending ? { ...d.counts, pending: Math.max(0, (d.counts.pending ?? 0) - 1), link_sent: (d.counts.link_sent ?? 0) + 1 } : d.counts,
      } : d);
      if (wasPending) onCounts?.((c) => Math.max(0, c - 1));
      toast.success("Reset link generated — copy it and send it to the user on WhatsApp");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusyId(""); }
  };

  const setManual = async (r) => {
    const pw = window.prompt(`Set a new login password for ${r.user_name || "user"} (+91 ${r.phone}).\nMinimum 6 characters. Share it with the user on WhatsApp manually.`);
    if (!pw) return;
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusyId(r.request_id);
    const wasPending = r.status === "pending";
    try {
      await api.post(`/admin/password-resets/${r.request_id}/set-password`, { new_password: pw });
      toast.success("Password updated — share it with the user on WhatsApp");
      setData((d) => d ? {
        ...d,
        requests: d.requests.map((x) => x.request_id === r.request_id ? { ...x, status: "completed" } : x),
        counts: d.counts && wasPending ? { ...d.counts, pending: Math.max(0, (d.counts.pending ?? 0) - 1) } : d.counts,
      } : d);
      if (wasPending) onCounts?.((c) => Math.max(0, c - 1));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusyId(""); }
  };

  const copy = async (rid) => {
    try { await navigator.clipboard.writeText(links[rid]); setCopied(rid); setTimeout(() => setCopied(""), 2000); }
    catch { toast.error("Copy failed — select the link manually"); }
  };

  const waUrl = (r) => `https://wa.me/91${r.phone}?text=${encodeURIComponent(`GulMatch password reset (valid 30 min, one-time use): ${links[r.request_id]}`)}`;

  return (
    <div data-testid="admin-password-resets">
      <div className="flex gap-2 mb-4 flex-wrap">
        {[["pending", "Pending"], ["link_sent", "Link generated"], ["all", "All"]].map(([s, label]) => (
          <button key={s} data-testid={`pr-filter-${s}`} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-md text-sm ${filter === s ? "bg-gray-900 text-white" : "border border-gray-300 text-gray-600"}`}>
            {label}{data?.counts && s !== "all" ? ` (${data.counts[s] ?? 0})` : ""}
          </button>
        ))}
      </div>
      <p className="text-xs text-gray-500 mb-3">Users submit reset requests from the login page (the same number can request multiple times — every request is listed). Generate a secure one-time link (valid 30 minutes), copy it, and send it to the user's WhatsApp manually.</p>
      {!data ? <Spinner /> : (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="w-full text-sm min-w-[760px]">
            <thead className="bg-gray-50"><tr><Th>User</Th><Th>Phone</Th><Th>Requested</Th><Th>Status</Th><Th>Reset link</Th></tr></thead>
            <tbody>
              {data.requests.map((r) => (
                <tr key={r.request_id} data-testid={`pr-row-${r.request_id}`} className="hover:bg-gray-50 align-top">
                  <Td className="font-medium text-gray-900">{r.user_name || "—"}</Td>
                  <Td>+91 {r.phone}</Td>
                  <Td className="text-gray-500 text-xs">{fmtDate(r.created_at)}</Td>
                  <Td>{r.status === "link_sent"
                    ? <span className="inline-block text-xs font-medium px-2 py-0.5 rounded border bg-blue-50 text-blue-700 border-blue-200">Link generated</span>
                    : <Badge status={r.status} />}</Td>
                  <Td>
                    {(r.status === "pending" || r.status === "link_sent") && (links[r.request_id] ? (
                      <div className="space-y-1.5 max-w-[340px]">
                        <code data-testid={`pr-link-${r.request_id}`} className="block text-xs bg-gray-50 border border-gray-200 rounded px-2 py-1.5 break-all text-gray-700">{links[r.request_id]}</code>
                        <div className="flex gap-1.5">
                          <button data-testid={`pr-copy-${r.request_id}`} onClick={() => copy(r.request_id)} className="px-2.5 py-1 text-xs border border-gray-900 text-gray-900 rounded font-medium flex items-center gap-1">
                            {copied === r.request_id ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} {copied === r.request_id ? "Copied" : "Copy"}
                          </button>
                          <a data-testid={`pr-whatsapp-${r.request_id}`} href={waUrl(r)} target="_blank" rel="noreferrer"
                            className="px-2.5 py-1 text-xs border border-emerald-400 text-emerald-700 rounded font-medium">Open WhatsApp</a>
                        </div>
                        <p className="text-[10px] text-gray-400">One-time use · expires 30 min after generation</p>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-1.5 items-start">
                        <button data-testid={`pr-generate-${r.request_id}`} onClick={() => generate(r)} disabled={busyId === r.request_id}
                          className="px-3 py-1.5 text-xs bg-gray-900 text-white rounded-md font-medium disabled:opacity-50 flex items-center gap-1.5">
                          {busyId === r.request_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
                          {r.status === "link_sent" ? "Regenerate link" : "Generate Reset Link"}
                        </button>
                        <button data-testid={`pr-manual-${r.request_id}`} onClick={() => setManual(r)} disabled={busyId === r.request_id}
                          className="px-3 py-1.5 text-xs border border-gray-300 text-gray-700 rounded-md font-medium disabled:opacity-50">
                          Set New Password Manually
                        </button>
                      </div>
                    ))}
                    {(r.status === "completed" || r.status === "superseded") && <span className="text-gray-400 text-xs">—</span>}
                  </Td>
                </tr>
              ))}
              {data.requests.length === 0 && <tr><Td className="text-gray-400">No password reset requests</Td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------------- Bots ---------------- */
function BotsSection() {
  const [bots, setBots] = useState(null);
  const [edit, setEdit] = useState(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/bots"); setBots(data.bots); } catch { toast.error("Failed to load"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const reseed = async () => {
    if (!window.confirm("Replace ALL bots with 50 fresh demo profiles? Existing bot chats/matches are removed.")) return;
    setBusy(true);
    try { const { data } = await api.post("/admin/bots/reseed"); toast.success(`Seeded ${data.seeded} (removed ${data.removed})`); load(); }
    catch { toast.error("Reseed failed"); } finally { setBusy(false); }
  };
  const toggle = async (b) => {
    try { await api.put(`/admin/bots/${b.user_id}`, { is_active: b.is_active === false }); load(); } catch { toast.error("Failed"); }
  };
  const del = async (b) => { if (!window.confirm(`Delete bot ${b.name}?`)) return; try { await api.delete(`/admin/bots/${b.user_id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); } };

  return (
    <div data-testid="admin-bots">
      <div className="flex gap-2 mb-4">
        <button data-testid="bot-add" onClick={() => setCreating(true)} className="px-4 py-2 bg-gray-900 text-white rounded-md text-sm font-medium flex items-center gap-1.5"><Plus className="w-4 h-4" /> Add bot</button>
        <button data-testid="bot-reseed" onClick={reseed} disabled={busy} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium flex items-center gap-1.5 disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Reseed 50</button>
      </div>
      {!bots ? <Spinner /> : (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-gray-50"><tr><Th>Name</Th><Th>Age</Th><Th>Gender</Th><Th>Location</Th><Th>Photos</Th><Th>Status</Th><Th>Actions</Th></tr></thead>
            <tbody>
              {bots.map((b) => (
                <tr key={b.user_id} data-testid={`bot-row-${b.user_id}`} className="hover:bg-gray-50">
                  <Td className="font-medium text-gray-900">{b.name}</Td><Td>{b.age}</Td><Td className="capitalize">{b.gender}</Td>
                  <Td>{b.neighborhood || "—"}</Td><Td>{b.photos?.length || 0}</Td>
                  <Td><Badge status={b.is_active === false ? "disabled" : "active"} /></Td>
                  <Td>
                    <div className="flex gap-1">
                      <button data-testid={`bot-edit-${b.user_id}`} onClick={() => setEdit(b)} className="p-1.5 text-gray-600 hover:bg-gray-100 rounded" title="Edit"><Pencil className="w-4 h-4" /></button>
                      <button data-testid={`bot-toggle-${b.user_id}`} onClick={() => toggle(b)} className="p-1.5 text-gray-600 hover:bg-gray-100 rounded" title="Enable/disable"><Power className="w-4 h-4" /></button>
                      <button data-testid={`bot-delete-${b.user_id}`} onClick={() => del(b)} className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="Delete"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {(edit || creating) && <BotEditor bot={edit} onClose={() => { setEdit(null); setCreating(false); }} onSaved={() => { setEdit(null); setCreating(false); load(); }} onListRefresh={load} />}
    </div>
  );
}

function BotEditor({ bot, onClose, onSaved, onListRefresh }) {
  const isNew = !bot;
  const [f, setF] = useState(bot || { name: "", age: 24, gender: "female", neighborhood: "City Centre", bio: "", interests: [] });
  const [photoUrl, setPhotoUrl] = useState("");
  const [photos, setPhotos] = useState(bot?.photos || []);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoDeleting, setPhotoDeleting] = useState(null);
  const [saving, setSaving] = useState(false);
  const inp = "w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-gray-900";

  useEffect(() => { setPhotos(bot?.photos || []); }, [bot]);

  // Photo add/delete keeps the editor open and refreshes photos + the bots table in place.
  const refreshPhotos = async () => {
    try {
      const { data } = await api.get("/admin/bots");
      const fresh = (data.bots || []).find((b) => b.user_id === bot.user_id);
      if (fresh) setPhotos(fresh.photos || []);
    } catch {}
    onListRefresh?.();
  };

  const save = async () => {
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/admin/bots", { name: f.name, age: Number(f.age), gender: f.gender, neighborhood: f.neighborhood, bio: f.bio, interests: f.interests || [] });
      } else {
        await api.put(`/admin/bots/${bot.user_id}`, { name: f.name, age: Number(f.age), gender: f.gender, neighborhood: f.neighborhood, bio: f.bio, interests: f.interests });
      }
      toast.success(isNew ? "Bot created" : "Bot updated"); onSaved();
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };
  const addPhoto = async () => {
    if (!photoUrl.trim() || isNew) { toast.error(isNew ? "Save the bot first" : "Enter a URL"); return; }
    try { await api.post(`/admin/bots/${bot.user_id}/photo-url`, { url: photoUrl.trim() }); toast.success("Photo added"); setPhotoUrl(""); refreshPhotos(); } catch { toast.error("Failed"); }
  };
  const onPickPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPhotoPreview(URL.createObjectURL(file)); // immediate preview
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/bots/${bot.user_id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploaded");
      refreshPhotos();
    } catch { toast.error("Upload failed"); }
    finally { setPhotoUploading(false); setPhotoPreview(null); }
  };
  const deletePhoto = async (photoId) => {
    if (photoDeleting) return;
    setPhotoDeleting(photoId);
    try {
      await api.delete(`/admin/bots/${bot.user_id}/photo/${photoId}`);
      toast.success("Photo removed");
      refreshPhotos();
    } catch { toast.error("Delete failed"); }
    finally { setPhotoDeleting(null); }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="bot-editor">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full max-w-md bg-white border-l border-gray-200 h-full overflow-y-auto">
        <div className="h-16 border-b border-gray-200 flex items-center justify-between px-5"><span className="text-sm font-semibold">{isNew ? "Create bot" : "Edit bot"}</span><button onClick={onClose}><X className="w-5 h-5 text-gray-500" /></button></div>
        <div className="p-5 space-y-3">
          <input data-testid="bot-field-name" className={inp} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Name" />
          <div className="flex gap-2">
            <input data-testid="bot-field-age" type="number" className={inp} value={f.age} onChange={(e) => setF({ ...f, age: e.target.value })} placeholder="Age" />
            <select data-testid="bot-field-gender" className={inp} value={f.gender} onChange={(e) => setF({ ...f, gender: e.target.value })}><option value="female">Female</option><option value="male">Male</option><option value="other">Other</option></select>
          </div>
          <input data-testid="bot-field-location" className={inp} value={f.neighborhood} onChange={(e) => setF({ ...f, neighborhood: e.target.value })} placeholder="Location" />
          <textarea data-testid="bot-field-bio" className={inp} rows={2} value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} placeholder="Bio" />
          <input data-testid="bot-field-interests" className={inp} value={(f.interests || []).join(", ")} onChange={(e) => setF({ ...f, interests: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} placeholder="Interests (comma separated)" />
          <button data-testid="bot-save" onClick={save} disabled={saving} className="w-full px-4 py-2 bg-gray-900 text-white rounded-md text-sm font-medium disabled:opacity-50">{saving ? "Saving…" : isNew ? "Create bot" : "Save changes"}</button>
          {!isNew && (
            <div className="pt-3 border-t border-gray-200">
              <p className="text-sm font-semibold mb-2">Photos ({photos.length + (photoPreview ? 1 : 0)})</p>
              <div className="flex gap-2 mb-2 flex-wrap">
                {photos.map((p) => (
                  <div key={p.id} className="relative w-14 h-14" data-testid={`bot-photo-${p.id}`}>
                    <img src={p.url} alt="" className="w-14 h-14 rounded object-cover border border-gray-200" />
                    <button data-testid={`bot-photo-delete-${p.id}`} onClick={() => deletePhoto(p.id)} disabled={photoDeleting === p.id}
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white grid place-items-center shadow disabled:opacity-50">
                      {photoDeleting === p.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
                    </button>
                  </div>
                ))}
                {photoPreview && (
                  <div className="relative w-14 h-14" data-testid="bot-photo-preview">
                    <img src={photoPreview} alt="" className="w-14 h-14 rounded object-cover border border-gray-200 opacity-70" />
                    <Loader2 className="absolute inset-0 m-auto w-5 h-5 animate-spin text-gray-700" />
                  </div>
                )}
                {photos.length === 0 && !photoPreview && (
                  <p className="text-xs text-gray-400 py-2">No photos yet — add one from Gallery below.</p>
                )}
              </div>
              <div className="flex gap-2">
                <input data-testid="bot-photo-url" className={inp} value={photoUrl} onChange={(e) => setPhotoUrl(e.target.value)} placeholder="Photo URL" />
                <button data-testid="bot-photo-add" onClick={addPhoto} className="px-3 py-2 border border-gray-300 rounded-md text-sm">Add</button>
              </div>
              <label data-testid="bot-photo-upload-btn"
                className="mt-2 w-full px-3 py-2 border border-dashed border-gray-300 rounded-md text-sm text-gray-600 flex items-center justify-center gap-2 cursor-pointer active:bg-gray-50">
                <ImagePlus className="w-4 h-4" />{photoUploading ? "Uploading…" : "Add Photo from Gallery"}
                <input type="file" accept="image/*" data-testid="bot-photo-upload-input" className="sr-only" onChange={onPickPhoto} disabled={photoUploading} />
              </label>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Deposits ---------------- */
function DepositsSection() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/deposits", { params: { limit: 100 } }).then(({ data }) => setD(data)).catch(() => toast.error("Failed")); }, []);
  if (!d) return <Spinner />;
  return (
    <div data-testid="admin-deposits" className="overflow-x-auto border border-gray-200 rounded-lg">
      <table className="w-full text-sm min-w-[720px]">
        <thead className="bg-gray-50"><tr><Th>User</Th><Th>Phone</Th><Th>Amount</Th><Th>Bonus</Th><Th>Payment ID</Th><Th>Status</Th><Th>Date</Th></tr></thead>
        <tbody>
          {d.deposits.map((x) => (
            <tr key={x.order_id || x.razorpay_order_id} className="hover:bg-gray-50">
              <Td className="font-medium">{x.user?.name || "—"}</Td><Td>{x.user?.phone || "—"}</Td>
              <Td>{money(x.amount)}</Td>
              <Td data-testid={`deposit-bonus-${x.order_id || x.razorpay_order_id}`}>{x.bonus_amount ? <span className="text-emerald-700 font-semibold">+{money(x.bonus_amount)} <span className="text-[10px] text-gray-400 font-normal">1st deposit</span></span> : <span className="text-gray-400">—</span>}</Td>
              <Td className="text-xs text-gray-500">{x.payment_id || x.order_id || x.razorpay_payment_id || x.razorpay_order_id}</Td>
              <Td><Badge status={x.status} /></Td><Td className="text-gray-500 text-xs">{fmtDate(x.paid_at || x.created_at)}</Td>
            </tr>
          ))}
          {d.deposits.length === 0 && <tr><Td className="text-gray-400">No deposits yet</Td></tr>}
        </tbody>
      </table>
    </div>
  );
}

/* ---------------- Withdrawals ---------------- */
function WithdrawalsSection() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("pending");
  const load = useCallback(async () => {
    setData(null);
    try { const { data } = await api.get("/admin/withdrawals", { params: filter === "all" ? {} : { status: filter } }); setData(data); }
    catch { toast.error("Failed"); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const act = async (w, action) => {
    let note = "";
    if (action === "reject") note = window.prompt("Reason (optional):") ?? "";
    try { await api.put(`/admin/withdrawals/${w.withdrawal_id}`, { action, admin_note: note }); toast.success(`Withdrawal ${action}d`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="admin-withdrawals">
      <div className="flex gap-2 mb-4 flex-wrap">
        {["pending", "approved", "completed", "rejected", "all"].map((s) => (
          <button key={s} data-testid={`wd-filter-${s}`} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-md text-sm capitalize ${filter === s ? "bg-gray-900 text-white" : "border border-gray-300 text-gray-600"}`}>
            {s}{data?.counts && s !== "all" ? ` (${data.counts[s] ?? 0})` : ""}
          </button>
        ))}
      </div>
      {!data ? <Spinner /> : (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="w-full text-sm min-w-[860px]">
            <thead className="bg-gray-50"><tr><Th>User</Th><Th>Amount</Th><Th>UPI</Th><Th>Holder</Th><Th>Bank</Th><Th>Status</Th><Th>Date</Th><Th>Actions</Th></tr></thead>
            <tbody>
              {data.withdrawals.map((w) => (
                <tr key={w.withdrawal_id} data-testid={`wd-row-${w.withdrawal_id}`} className="hover:bg-gray-50">
                  <Td><div className="font-medium">{w.user?.name || "—"}</div><div className="text-xs text-gray-500">{w.user?.phone}</div></Td>
                  <Td className="font-semibold">{money(w.amount)}</Td><Td>{w.upi_id}</Td><Td>{w.account_holder}</Td>
                  <Td className="text-xs text-gray-500">{w.bank_account ? `${w.bank_account} / ${w.ifsc}` : "—"}</Td>
                  <Td><Badge status={w.status} /></Td><Td className="text-gray-500 text-xs">{fmtDate(w.created_at)}</Td>
                  <Td>
                    {(w.status === "pending" || w.status === "approved") ? (
                      <div className="flex gap-1">
                        {w.status === "pending" && <button data-testid={`wd-approve-${w.withdrawal_id}`} onClick={() => act(w, "approve")} className="px-2 py-1 text-xs border border-blue-300 text-blue-700 rounded">Approve</button>}
                        {w.status === "approved" && <button data-testid={`wd-complete-${w.withdrawal_id}`} onClick={() => act(w, "complete")} className="px-2 py-1 text-xs border border-green-300 text-green-700 rounded">Mark paid</button>}
                        <button data-testid={`wd-reject-${w.withdrawal_id}`} onClick={() => act(w, "reject")} className="px-2 py-1 text-xs border border-red-300 text-red-700 rounded">Reject</button>
                      </div>
                    ) : <span className="text-xs text-gray-400">—</span>}
                  </Td>
                </tr>
              ))}
              {data.withdrawals.length === 0 && <tr><Td className="text-gray-400">No withdrawals</Td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------------- Matches ---------------- */
function MatchesSection() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/matches", { params: { limit: 100 } }).then(({ data }) => setD(data)).catch(() => toast.error("Failed")); }, []);
  if (!d) return <Spinner />;
  return (
    <div data-testid="admin-matches">
      <p className="text-xs text-gray-500 mb-2">{d.total} matches</p>
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50"><tr><Th>User A</Th><Th>User B</Th><Th>Messages</Th><Th>Created</Th></tr></thead>
          <tbody>
            {d.matches.map((m) => (
              <tr key={m.match_id} className="hover:bg-gray-50">
                <Td>{m.user_a?.name || "—"} {m.user_a?.is_bot ? <span className="text-xs text-gray-400">(bot)</span> : ""}</Td>
                <Td>{m.user_b?.name || "—"} {m.user_b?.is_bot ? <span className="text-xs text-gray-400">(bot)</span> : ""}</Td>
                <Td>{m.message_count}</Td><Td className="text-gray-500 text-xs">{fmtDate(m.created_at)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------------- Chats ---------------- */
function ChatsSection() {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/admin/chats", { params: { limit: 100 } }).then(({ data }) => setD(data.chats)).catch(() => toast.error("Failed")); }, []);
  if (!d) return <Spinner />;
  return (
    <div data-testid="admin-chats">
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50"><tr><Th>Participants</Th><Th>Messages</Th><Th>Last message</Th><Th>Last activity</Th><Th></Th></tr></thead>
          <tbody>
            {d.map((c) => (
              <tr key={c.match_id} data-testid={`chat-row-${c.match_id}`} className="hover:bg-gray-50">
                <Td>{c.user_a?.name || "—"} ↔ {c.user_b?.name || "—"}</Td>
                <Td>{c.message_count}</Td><Td className="max-w-[240px] truncate text-gray-600">{c.last_text}</Td>
                <Td className="text-gray-500 text-xs">{fmtDate(c.last_at)}</Td>
                <Td><button data-testid={`chat-view-${c.match_id}`} onClick={() => setOpen(c.match_id)} className="text-xs text-gray-900 underline">View</button></Td>
              </tr>
            ))}
            {d.length === 0 && <tr><Td className="text-gray-400">No chats yet</Td></tr>}
          </tbody>
        </table>
      </div>
      {open && <ChatViewer matchId={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

function ChatViewer({ matchId, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/admin/chats/${matchId}/messages`).then(({ data }) => setD(data)).catch(() => toast.error("Failed")); }, [matchId]);
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="chat-viewer">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full max-w-md bg-white border-l border-gray-200 h-full overflow-y-auto">
        <div className="h-16 border-b border-gray-200 flex items-center justify-between px-5 sticky top-0 bg-white"><span className="text-sm font-semibold">{d ? `${d.user_a?.name} ↔ ${d.user_b?.name}` : "Chat"}</span><button onClick={onClose}><X className="w-5 h-5 text-gray-500" /></button></div>
        {!d ? <Spinner /> : (
          <div className="p-5 space-y-2">
            {d.messages.map((m) => {
              const mine = m.sender_id === d.user_a?.user_id;
              const name = mine ? d.user_a?.name : d.user_b?.name;
              return (
                <div key={m.message_id} className={`flex flex-col ${mine ? "items-start" : "items-end"}`}>
                  <div className={`max-w-[80%] px-3 py-2 rounded-lg text-sm ${mine ? "bg-gray-100 text-gray-800" : "bg-gray-900 text-white"}`}>{m.text}</div>
                  <span className="text-[10px] text-gray-400 mt-0.5">{name} · {fmtDate(m.created_at)}</span>
                </div>
              );
            })}
            {d.messages.length === 0 && <p className="text-xs text-gray-400">No messages</p>}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- Activity Log ---------------- */
function LogsSection() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const load = useCallback(async (action = "") => {
    setData(null);
    try { const { data } = await api.get("/admin/logs", { params: { action, limit: 100 } }); setData(data); }
    catch { toast.error("Failed to load logs"); }
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="admin-logs">
      <form onSubmit={(e) => { e.preventDefault(); load(filter); }} className="flex gap-2 mb-4 max-w-md">
        <input data-testid="log-filter-input" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter by action (e.g. balance, withdrawal, bot)"
          className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-gray-900" />
        <button className="px-4 py-2 bg-gray-900 text-white rounded-md text-sm font-medium">Filter</button>
      </form>
      {!data ? <Spinner /> : (
        <>
          <p className="text-xs text-gray-500 mb-2">{data.total} actions logged</p>
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-gray-50"><tr><Th>When</Th><Th>Admin</Th><Th>Action</Th><Th>Details</Th></tr></thead>
              <tbody>
                {data.logs.map((l) => (
                  <tr key={l.log_id} data-testid="log-row" className="hover:bg-gray-50">
                    <Td className="text-gray-500 text-xs whitespace-nowrap">{fmtDate(l.created_at)}</Td>
                    <Td>{l.admin_name || "admin"}</Td>
                    <Td><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">{l.action}</code></Td>
                    <Td className="text-gray-700">{l.summary}</Td>
                  </tr>
                ))}
                {data.logs.length === 0 && <tr><Td className="text-gray-400">No actions logged yet</Td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

/* ---------------- Settings ---------------- */
function SettingsSection() {
  const [links, setLinks] = useState({ telegram_link: "", whatsapp_link: "" });
  const [ai, setAi] = useState(true);
  const [pct, setPct] = useState(80);
  const [botStats, setBotStats] = useState({ bots_total: 0, bots_responsive: 0 });
  const [savingPct, setSavingPct] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/support/config"), api.get("/admin/ai-settings")])
      .then(([l, a]) => {
        setLinks(l.data); setAi(a.data.bot_replies);
        setPct(a.data.bot_responsive_pct ?? 80);
        setBotStats({ bots_total: a.data.bots_total || 0, bots_responsive: a.data.bots_responsive || 0 });
        setLoaded(true);
      })
      .catch(() => { setLoaded(true); });
  }, []);

  const saveLinks = async () => { try { await api.put("/admin/support-config", links); toast.success("Links saved"); } catch { toast.error("Failed"); } };
  const toggleAi = async () => { const n = !ai; setAi(n); try { await api.put("/admin/ai-settings", { bot_replies: n }); toast.success("Saved"); } catch { setAi(!n); toast.error("Failed"); } };
  const savePct = async () => {
    setSavingPct(true);
    try {
      const { data } = await api.put("/admin/ai-settings", { bot_responsive_pct: Number(pct) });
      setBotStats({ bots_total: data.bots_total || 0, bots_responsive: data.bots_responsive || 0 });
      toast.success(`${data.bots_responsive}/${data.bots_total} bots responsive`);
    } catch { toast.error("Failed"); }
    finally { setSavingPct(false); }
  };

  if (!loaded) return <Spinner />;
  const inp = "w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-gray-900";
  return (
    <div data-testid="admin-settings" className="max-w-lg space-y-8">
      <div>
        <h3 className="text-sm font-semibold mb-2 text-gray-900">Support & Earn links</h3>
        <div className="space-y-2">
          <input data-testid="settings-telegram" className={inp} value={links.telegram_link || ""} onChange={(e) => setLinks({ ...links, telegram_link: e.target.value })} placeholder="Telegram link" />
          <input data-testid="settings-whatsapp" className={inp} value={links.whatsapp_link || ""} onChange={(e) => setLinks({ ...links, whatsapp_link: e.target.value })} placeholder="WhatsApp link" />
          <button data-testid="settings-save-links" onClick={saveLinks} className="px-4 py-2 bg-gray-900 text-white rounded-md text-sm font-medium">Save links</button>
        </div>
      </div>
      <div>
        <h3 className="text-sm font-semibold mb-2 text-gray-900">AI bot auto-replies</h3>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input data-testid="settings-ai-toggle" type="checkbox" checked={ai} onChange={toggleAi} />
          Demo/bot profiles reply in-character using Gemini
        </label>
      </div>
      <div>
        <h3 className="text-sm font-semibold mb-1 text-gray-900">Bot response availability</h3>
        <p className="text-xs text-gray-500 mb-2">
          What % of bot profiles should respond to messages. The rest still appear in the pool but never auto-reply.
          Currently <span className="font-semibold text-gray-700" data-testid="settings-bot-stats">{botStats.bots_responsive}/{botStats.bots_total}</span> responsive.
        </p>
        <div className="flex items-center gap-3">
          <input data-testid="settings-responsive-pct" type="number" min="0" max="100" value={pct}
            onChange={(e) => setPct(e.target.value)} className="w-24 px-3 py-2 border border-gray-300 rounded-md text-sm" />
          <span className="text-sm text-gray-600">%</span>
          <button data-testid="settings-apply-pct" onClick={savePct} disabled={savingPct}
            className="px-4 py-2 bg-gray-900 text-white rounded-md text-sm font-medium disabled:opacity-60">
            {savingPct ? "Applying…" : "Apply"}
          </button>
        </div>
      </div>
    </div>
  );
}
